import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { keepPreviousData, useQueryClient, useQuery as useTanstackQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import qs from 'qs';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Container, Typography } from '@mui/material';

import PromoAiWorkflowEntry from '~/components/Common/Blocks/PromoAiWorkflowEntry';
import usePromoMeta from '~/components/Promo/hooks/usePromoMeta';

import { useDeleteVoucher } from '~/hooks/apis/promo/useDeleteVoucher';
import { useGetExtendedVoucherWarnings } from '~/hooks/apis/promo/useGetExtendedVoucherWarnings';
import { useVoucherAuditUsers } from '~/hooks/apis/promo/useVoucherAuditUsers';
import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentPromoBrand from '~/hooks/useCurrentPromoBrand';
import useCurrentTenant from '~/hooks/useCurrentTenant';
import useDebounced from '~/hooks/useDebounced';
import useQuery from '~/hooks/useQuery';

import { ListVouchersQuery, ListVouchersResponse, PromoMeta, Voucher, listVouchers } from '~/services/PromoService';

import VoucherUserLookup from './VoucherUserLookup';
import VouchersTab from './VouchersTab';
import { parseCaseIdFilterQueryParam, serializeCaseIdFilterForApi } from './caseIdFilterTokens';
import { useVoucherToggleFilter } from './useVoucherToggleFilter';
import {
  VoucherListSortBy,
  VoucherListSortDirection,
  parseVoucherListSortBy,
  parseVoucherListSortDirection,
} from './voucherListSorting';

type VoucherReportingTagDefinition = NonNullable<
  PromoMeta['global_promo_config']
>['voucherReportingTagsConfig'][number];

function parseReportingTagsQueryParam(value: string | null): Array<string> {
  if (!value) {
    return [];
  }
  return value
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
}

function reportingTagOptionsFromPromoMeta(
  config: Array<VoucherReportingTagDefinition> | undefined,
): Array<{ value: string; label: string }> {
  if (!config) {
    return [];
  }
  return config.map((def) => ({
    value: def.tag,
    label: def.short_description?.trim() ? def.short_description : def.tag,
  }));
}

function defaultExcludedReportingTagsFromPromoMeta(
  config: Array<VoucherReportingTagDefinition> | undefined,
): Array<string> {
  return (
    config?.filter((definition) => definition.isFilteredOutOfAdminListByDefault).map((definition) => definition.tag) ??
    []
  );
}

export default function VouchersContainer() {
  const { hash } = useLocation();
  const query = useQuery();
  const lookupUserId = query.get('user_id')?.trim() ?? '';
  const { push } = useAppNavigation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const tenant = useCurrentTenant();
  const { promoMeta, isPromoMetaLoading } = usePromoMeta();
  const deleteVoucherMutation = useDeleteVoucher();
  const [showExtendedWarnings, setShowExtendedWarnings] = useState(false);

  useEffect(() => {
    if (hash === '#voucher-users') {
      document.getElementById('voucher-users')?.scrollIntoView();
    }
  }, [hash]);

  const [pageNum, setPageNum] = useState<number>(query.get('page_num') ? Number(query.get('page_num')) : 0);
  const [pageSize, setPageSize] = useState<number>(query.get('page_size') ? Number(query.get('page_size')) : 20);
  const [voucherName, setVoucherName] = useState<string>(query.get('voucher_name') || '');
  const [selectedReportingTags, setSelectedReportingTags] = useState<Array<string>>(() =>
    parseReportingTagsQueryParam(query.get('voucher_reporting_tags')),
  );
  const [excludedReportingTags, setExcludedReportingTags] = useState<Array<string>>(() =>
    parseReportingTagsQueryParam(query.get('voucher_reporting_tags_exclude')),
  );
  const [hasExplicitExcludedReportingTags, setHasExplicitExcludedReportingTags] = useState(() =>
    query.has('voucher_reporting_tags_exclude'),
  );
  const [adminUserId, setAdminUserId] = useState<string>(() => query.get('admin_user_id')?.trim() ?? '');
  const [adminUserSearchValue, setAdminUserSearchValue] = useState<string>(
    () => query.get('admin_user_id')?.trim() ?? '',
  );
  const [sortBy, setSortBy] = useState<VoucherListSortBy | undefined>(() =>
    parseVoucherListSortBy(query.get('sort_by')),
  );
  const [sortByDirection, setSortByDirection] = useState<VoucherListSortDirection | undefined>(() =>
    parseVoucherListSortDirection(query.get('sort_by_direction')),
  );
  const [caseIdFilter, setCaseIdFilter] = useState<Array<string>>(() =>
    parseCaseIdFilterQueryParam(query.get('case_id')),
  );
  const debouncedCaseIdFilter = useDebounced(caseIdFilter, 400);
  const [onlyActive, setOnlyActive] = useState<boolean>(() => {
    if (parseCaseIdFilterQueryParam(query.get('case_id')).length > 0) {
      return false;
    }
    return query.get('only_active') !== 'false';
  });

  const caseIdFilterApi = useMemo(() => serializeCaseIdFilterForApi(caseIdFilter), [caseIdFilter]);
  const caseIdFilterApiForQuery = useMemo(
    () => serializeCaseIdFilterForApi(debouncedCaseIdFilter),
    [debouncedCaseIdFilter],
  );
  const hasCaseIdFilter = caseIdFilter.length > 0;
  const hasCaseIdFilterForQuery = debouncedCaseIdFilter.length > 0;
  const effectiveOnlyActive = hasCaseIdFilter ? false : onlyActive;
  const isCaseIdFilterDebouncing = caseIdFilterApi !== caseIdFilterApiForQuery;

  useEffect(() => {
    if (hasCaseIdFilter) {
      setOnlyActive(false);
    }
  }, [hasCaseIdFilter]);

  const reportingTagOptions = useMemo(
    () => reportingTagOptionsFromPromoMeta(promoMeta?.global_promo_config?.voucherReportingTagsConfig),
    [promoMeta?.global_promo_config?.voucherReportingTagsConfig],
  );
  const defaultExcludedReportingTags = useMemo(
    () => defaultExcludedReportingTagsFromPromoMeta(promoMeta?.global_promo_config?.voucherReportingTagsConfig),
    [promoMeta?.global_promo_config?.voucherReportingTagsConfig],
  );
  const effectiveExcludedReportingTags = hasExplicitExcludedReportingTags
    ? excludedReportingTags
    : defaultExcludedReportingTags;

  const brand = useCurrentPromoBrand();

  const queryParams: ListVouchersQuery = useMemo(() => {
    return {
      page: pageNum,
      limit: pageSize,
      brand: brand.promoBrand,
      only_active: effectiveOnlyActive,
      ...(voucherName ? { voucher_name: voucherName } : {}),
      ...(selectedReportingTags.length > 0 ? { voucher_reporting_tags: selectedReportingTags } : {}),
      ...(effectiveExcludedReportingTags.length > 0
        ? { voucher_reporting_tags_exclude: effectiveExcludedReportingTags }
        : {}),
      ...(adminUserId ? { userId: adminUserId } : {}),
      ...(sortBy ? { sortBy, ...(sortByDirection ? { sortByDirection } : {}) } : {}),
      ...(hasCaseIdFilterForQuery ? { case_id_filter: caseIdFilterApiForQuery } : {}),
    };
  }, [
    pageNum,
    pageSize,
    voucherName,
    selectedReportingTags,
    effectiveExcludedReportingTags,
    adminUserId,
    sortBy,
    sortByDirection,
    caseIdFilterApiForQuery,
    hasCaseIdFilterForQuery,
    effectiveOnlyActive,
    tenant.tenant.brand,
  ]);

  const setQueryParams = useCallback(() => {
    const searchParams = qs.stringify({
      page_num: pageNum.toString(),
      page_size: pageSize.toString(),
      voucher_name: voucherName || undefined,
      voucher_reporting_tags: selectedReportingTags.length > 0 ? selectedReportingTags.join(',') : undefined,
      // An empty value records that the metadata-defined default exclusions were intentionally cleared.
      voucher_reporting_tags_exclude: hasExplicitExcludedReportingTags ? excludedReportingTags.join(',') : undefined,
      admin_user_id: adminUserId || undefined,
      user_id: lookupUserId || undefined,
      sort_by: sortBy,
      sort_by_direction: sortBy ? sortByDirection : undefined,
      brand: tenant.tenant.brand,
      case_id: hasCaseIdFilter ? caseIdFilter.join(',') : undefined,
      ...(!effectiveOnlyActive ? { only_active: 'false' } : {}),
    });
    push({ search: `?${searchParams}` });
  }, [
    push,
    pageNum,
    pageSize,
    voucherName,
    selectedReportingTags,
    excludedReportingTags,
    hasExplicitExcludedReportingTags,
    adminUserId,
    lookupUserId,
    sortBy,
    sortByDirection,
    hasCaseIdFilter,
    caseIdFilter,
    effectiveOnlyActive,
    tenant.tenant.brand,
  ]);

  const { data, isLoading, isFetching } = useTanstackQuery<ListVouchersResponse>({
    queryKey: [
      'vouchers',
      {
        pageNum,
        pageSize,
        voucherName,
        selectedReportingTags,
        effectiveExcludedReportingTags,
        adminUserId,
        sortBy,
        sortByDirection,
        caseIdFilterApiForQuery,
        effectiveOnlyActive,
        brand: tenant.tenant.brand,
      },
    ],
    queryFn: () => listVouchers(queryParams),
    enabled: !isPromoMetaLoading,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (hash !== '#voucher-users') {
      setQueryParams();
    }
  }, [hash, setQueryParams]);

  const onPageChange = useCallback((page: number, pageSize: number) => {
    setPageNum(page);
    setPageSize(pageSize);
  }, []);

  const updateVoucherName = useCallback((value: string) => {
    setVoucherName(value);
    setPageNum(0);
  }, []);

  const updateSelectedReportingTags = useCallback(
    (tags: Array<string>) => {
      setHasExplicitExcludedReportingTags(true);
      setSelectedReportingTags(tags);
      setExcludedReportingTags((current) => {
        const exclusions = hasExplicitExcludedReportingTags ? current : defaultExcludedReportingTags;
        return exclusions.filter((tag) => !tags.includes(tag));
      });
      setPageNum(0);
    },
    [defaultExcludedReportingTags, hasExplicitExcludedReportingTags],
  );

  const updateExcludedReportingTags = useCallback((tags: Array<string>) => {
    setHasExplicitExcludedReportingTags(true);
    setExcludedReportingTags(tags);
    setSelectedReportingTags((current) => current.filter((tag) => !tags.includes(tag)));
    setPageNum(0);
  }, []);

  const updateAdminUserId = useCallback((value: string) => {
    setAdminUserId(value.trim());
    setPageNum(0);
  }, []);

  const updateSort = useCallback((nextSortBy?: VoucherListSortBy, nextSortByDirection?: VoucherListSortDirection) => {
    setSortBy(nextSortBy);
    setSortByDirection(nextSortBy ? nextSortByDirection : undefined);
    setPageNum(0);
  }, []);

  const updateCaseIdFilter = useCallback((value: Array<string>) => {
    setCaseIdFilter(value);
    setPageNum(0);
  }, []);

  const updateOnlyActive = useCallback((value: boolean) => {
    setOnlyActive(value);
    setPageNum(0);
  }, []);

  const vouchers = data?.result?.vouchers ?? [];
  const voucherIds = useMemo(() => vouchers.map((voucher) => voucher.voucherId), [vouchers]);
  const extendedWarningsQuery = useGetExtendedVoucherWarnings({
    voucherIds,
    brand: brand.promoBrand,
    enabled: showExtendedWarnings,
  });
  const { clearVoucherToggle, filteredVouchers, selectedVoucherToggle, selectVoucherToggle } =
    useVoucherToggleFilter(vouchers);
  const clearAllFilters = useCallback(() => {
    setVoucherName('');
    setSelectedReportingTags([]);
    setHasExplicitExcludedReportingTags(true);
    setExcludedReportingTags([]);
    setAdminUserId('');
    setAdminUserSearchValue('');
    setSortBy(undefined);
    setSortByDirection(undefined);
    setCaseIdFilter([]);
    setOnlyActive(true);
    setPageNum(0);
    clearVoucherToggle();
  }, [clearVoucherToggle]);
  const { data: auditUsersById = new Map<string, App.UserSummary>() } = useVoucherAuditUsers({
    vouchers,
    brand: brand.promoBrand,
  });
  const totalCount = Number(data?.result?.total ?? 0);

  const handleDeleteVoucher = useCallback(
    async (voucher: Voucher) => {
      if (!voucher.voucherId) {
        enqueueSnackbar('Unable to delete voucher: missing voucher id.', { variant: 'error' });
        throw new Error('Missing voucher id');
      }
      try {
        await deleteVoucherMutation.mutateAsync({ voucherId: voucher.voucherId });
        enqueueSnackbar(`Deleted voucher ${voucher.voucherName || voucher.voucherId}.`, { variant: 'success' });
        queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      } catch (error) {
        enqueueSnackbar(`Failed to delete voucher: ${(error as Error).message}`, { variant: 'error' });
        throw error;
      }
    },
    [deleteVoucherMutation, enqueueSnackbar, queryClient],
  );

  return (
    <Container sx={{ width: '100%' }}>
      <Helmet>
        <title>Vouchers</title>
      </Helmet>
      <Box>
        <Box sx={{ mb: 2 }}>
          <PromoAiWorkflowEntry kind="voucher" />
        </Box>
        <Accordion
          defaultExpanded
          disableGutters
          elevation={0}
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">{`Vouchers [${filteredVouchers.length} of ${totalCount}]`}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: { xs: 1, sm: 2 }, pt: 0 }}>
            <VouchersTab
              voucherName={voucherName}
              setVoucherName={updateVoucherName}
              reportingTagOptions={reportingTagOptions}
              selectedReportingTags={selectedReportingTags}
              setSelectedReportingTags={updateSelectedReportingTags}
              excludedReportingTags={effectiveExcludedReportingTags}
              setExcludedReportingTags={updateExcludedReportingTags}
              adminUserId={adminUserId}
              setAdminUserId={updateAdminUserId}
              adminUserSearchValue={adminUserSearchValue}
              setAdminUserSearchValue={setAdminUserSearchValue}
              sortBy={sortBy}
              sortByDirection={sortByDirection}
              onSortChange={updateSort}
              caseIdFilter={caseIdFilter}
              setCaseIdFilter={updateCaseIdFilter}
              effectiveOnlyActive={effectiveOnlyActive}
              caseIdFilterActive={hasCaseIdFilter}
              setOnlyActive={updateOnlyActive}
              onClearAllFilters={clearAllFilters}
              data={filteredVouchers}
              showExtendedWarnings={showExtendedWarnings}
              onShowExtendedWarningsChange={setShowExtendedWarnings}
              extendedWarningsByVoucherId={showExtendedWarnings ? extendedWarningsQuery.data : undefined}
              isLoadingExtendedWarnings={showExtendedWarnings && extendedWarningsQuery.isLoading}
              extendedWarningsError={showExtendedWarnings ? extendedWarningsQuery.error : null}
              isLoading={isLoading || isFetching || isCaseIdFilterDebouncing}
              isDeleting={deleteVoucherMutation.isPending}
              totalCount={totalCount}
              pageNum={pageNum}
              pageSize={pageSize}
              onPageChange={onPageChange}
              onConfirmDelete={handleDeleteVoucher}
              auditUsersById={auditUsersById}
              selectedVoucherToggle={selectedVoucherToggle}
              onVoucherToggleClick={selectVoucherToggle}
              onClearVoucherToggle={clearVoucherToggle}
            />
          </AccordionDetails>
        </Accordion>
        <VoucherUserLookup showUsersAccordion />
      </Box>
    </Container>
  );
}
